import type { Fundamentals, HistoricalSeries, PricePoint, YearValue } from "@/lib/data/types";
import { QUALITY_CHECKLIST_THRESHOLDS, SCORE_CONFIG } from "@/lib/config";
import type { KpiKey, KpiScoreEntry, RedFlags, ScoreResult } from "@/lib/scoring/types";
import { average, cagrOverYearsWithMinimum, lastNValues } from "@/lib/stats";
import { joinWithUnd } from "@/lib/text";

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export interface ScoringInput {
  fundamentals: Fundamentals;
  historical: HistoricalSeries;
  priceHistory: PricePoint[];
  /** Kurzname der Aktie (z.B. "LVMH" statt der vollen Langform), fuer eine
   * unternehmensspezifische statt generische buildSummary()-Formulierung - optional,
   * andere Aufrufer (Wettbewerbsvergleich, Backtest, Score-Badges) lassen es weiterhin
   * weg und bekommen die bisherige generische Fassung. */
  name?: string;
}

const LABELS: Record<KpiKey, string> = {
  netMargin: "Nettomarge",
  roic: "ROIC",
  revenueCagr: "Umsatzwachstum",
  epsCagr: "Gewinnwachstum je Aktie",
  netDebtToEbitda: "Net Debt / EBITDA",
  fcfMargin: "FCF-Marge",
  valuation: "KGV vs. eigener Durchschnitt",
  capitalReturn: "Dividende",
  priceCagr: "Kursperformance",
  lossYears: "Bilanzrobustheit",
};

/** Nicht-technisches Themenfeld je Score-Bereich, fuer den Kurztext (buildSummary unten) -
 * bewusst KEINE rohen Kennzahlen-Labels/Akronyme wie "ROIC" oder "Net Debt / EBITDA"
 * (siehe Test "nicht-technischer Score-Kurztext"). */
const KPI_THEMES: Record<KpiKey, string> = {
  netMargin: "Profitabilität",
  roic: "Kapitalrendite",
  revenueCagr: "Umsatzwachstum",
  epsCagr: "Gewinnwachstum",
  netDebtToEbitda: "Verschuldung",
  fcfMargin: "Cashflow-Qualität",
  valuation: "Bewertung",
  capitalReturn: "Dividende",
  priceCagr: "Kursperformance",
  lossYears: "Bilanzrobustheit",
};

/** Kurze, allgemeinverstaendliche Erklaerung JE BEREICH - was gemessen wird und wo die
 * 0/10-Punkte-Grenzen liegen (aus SCORE_CONFIG.thresholds abgeleitet, siehe dortige
 * Werte). Ergaenzt (nicht ersetzt) den dynamischen, aktienspezifischen `reason`-Text aus
 * `entry()` oben - Erik-Feedback 2026-07-22: die bisherigen reason-Saetze allein wirkten
 * "zu statisch und komisch", weil sie direkt mit Zahlen/Fachbegriffen einsteigen, ohne
 * vorher zu erklaeren, WAS ueberhaupt gemessen wird. Bewusst statisch (nicht Teil von
 * `entry()`/der reinen Scoring-Berechnung) - eine allgemeine Methodik-Erklaerung braucht
 * keine Neuberechnung pro Aktie. */
export const KPI_METHOD_NOTES: Record<KpiKey, string> = {
  netMargin:
    "Wie viel vom Umsatz nach allen Kosten und Steuern als Gewinn übrig bleibt, im Schnitt der letzten bis zu 10 Jahre. 0 Punkte bei 3 % oder weniger, 10 Punkte ab 30 %.",
  roic:
    "Wie effizient das Unternehmen sein eingesetztes Kapital in Gewinn verwandelt (Return on Invested Capital), im Schnitt der letzten bis zu 10 Jahre. 0 Punkte bei 0 % oder weniger, 10 Punkte ab 15 %.",
  revenueCagr:
    "Durchschnittliches jährliches Umsatzwachstum über bis zu 10 Jahre (CAGR). 0 Punkte bei 0 % oder weniger, 10 Punkte ab 10 % pro Jahr.",
  epsCagr:
    "Durchschnittliches jährliches Wachstum des Gewinns je Aktie über bis zu 10 Jahre (CAGR). 0 Punkte bei 0 % oder weniger, 10 Punkte ab 15 % pro Jahr.",
  netDebtToEbitda:
    "Verhältnis von Nettoverschuldung zum operativen Gewinn (EBITDA) - zeigt, wie schnell die Schulden aus dem laufenden Geschäft tilgbar wären. 10 Punkte ab netto schuldenfrei, 0 Punkte ab dem 4-Fachen des EBITDA.",
  fcfMargin:
    "Wie viel vom Umsatz nach Investitionen als frei verfügbarer Cashflow übrig bleibt, im Schnitt der letzten bis zu 10 Jahre. 0 Punkte bei 0 % oder weniger, 10 Punkte ab 15 %.",
  valuation:
    "Vergleicht das aktuelle Kurs-Gewinn-Verhältnis mit dem eigenen historischen Durchschnitt der letzten bis zu 10 Jahre - günstiger als sonst gibt mehr Punkte. 10 Punkte ab 25 % günstiger als der eigene Schnitt, 0 Punkte ab 25 % teurer.",
  capitalReturn:
    "Kombiniert aktuelle Dividendenrendite und Dividendenwachstum (mind. 5 Jahre Historie nötig) je zur Hälfte. Volle Punktzahl je Teilscore ab 3 % Rendite bzw. 10 % Wachstum pro Jahr.",
  priceCagr:
    "Durchschnittliche jährliche Kursentwicklung über bis zu 10 Jahre (CAGR). 0 Punkte bei 0 % oder weniger, 10 Punkte ab 12 % pro Jahr.",
  lossYears:
    "Anteil der Jahre mit einem Nettoverlust an den letzten bis zu 10 betrachteten Jahren - je weniger Verlustjahre, desto mehr Punkte.",
};

/** Baut "Besonders stark im Bereich X." / "Schwächer in den Bereichen X und Y." aus den
 * (bereits nach Punktzahl sortierten) staerksten/schwaechsten Bereichen - dedupliziert
 * defensiv, falls sich mehrere Bereiche je Aufruf ein Themenfeld teilen sollten, und
 * waehlt automatisch Singular/Plural-Grammatik. */
function describeThemes(entries: KpiScoreEntry[], verb: string): string {
  const themes = [...new Set(entries.map((e) => KPI_THEMES[e.key]))];
  if (themes.length === 0) return "";
  const location = themes.length === 1 ? `im Bereich ${themes[0]}` : `in den Bereichen ${joinWithUnd(themes)}`;
  return `${verb} ${location}.`;
}

function entry(key: KpiKey, basePoints: number | undefined, reason: string): KpiScoreEntry {
  return {
    key,
    label: LABELS[key],
    basePoints,
    reason,
    available: basePoints !== undefined,
  };
}

/** Lineare Interpolation zwischen badX (0 Pkt.) und goodX (10 Pkt.), geklemmt auf [0,10] -
 * funktioniert unveraendert fuer invertierte Baender (badX > goodX, z.B. Verschuldung). */
function gradePoints(value: number, badX: number, goodX: number): number {
  return clamp((10 * (value - badX)) / (goodX - badX), 0, 10);
}

/** Bereiche 1/2 (Nettomarge, ROIC): einfacher Durchschnitt einer bereits als Verhaeltnis
 * vorliegenden Jahresserie ueber bis zu idealYears Jahre (min. minYears, sonst faellt der
 * Bereich komplett heraus statt mit 0 bewertet zu werden - kein Sonderfall, derselbe
 * Renormalisierungs-Mechanismus wie bei jedem anderen fehlenden Bereich, siehe
 * computeScore unten). Keine Stabilitaets-/Streuungspruefung, keine Sektor-Anpassung -
 * reine Lineartransformation. */
function scoreAverageRatio(
  key: KpiKey,
  series: YearValue[],
  cfg: { badX: number; goodX: number; idealYears: number; minYears: number },
  metricLabel: string,
): KpiScoreEntry {
  const values = lastNValues(series, cfg.idealYears);
  if (values.length < cfg.minYears) {
    return entry(key, undefined, `Für ${metricLabel} liegen keine ${cfg.minYears} Jahre vor.`);
  }
  const avg = average(values)!;
  const points = gradePoints(avg, cfg.badX, cfg.goodX);
  return entry(
    key,
    points,
    `${metricLabel} Ø ${(avg * 100).toFixed(1)} % über ${values.length} verfügbare Jahre liegt im Band von ${(cfg.badX * 100).toFixed(0)} % bis ${(cfg.goodX * 100).toFixed(0)} %.`,
  );
}

/** Verknuepft zwei Jahresserien (z.B. freier Cashflow und Umsatz fuer Bereich 6) zu einer
 * Verhaeltnis-Serie fuer genau die Jahre, in denen beide vorliegen und der Nenner positiv
 * ist - Grundlage fuer scoreAverageRatio oben, wenn die Kennzahl (anders als Nettomarge/
 * ROIC) nicht bereits als fertige Jahresquote vorliegt. */
function deriveRatioSeries(numerator: YearValue[], denominator: YearValue[]): YearValue[] {
  const denomByYear = new Map(denominator.map((v) => [v.year, v.value]));
  const out: YearValue[] = [];
  for (const n of numerator) {
    const d = denomByYear.get(n.year);
    if (d !== undefined && d > 0) out.push({ year: n.year, value: n.value / d });
  }
  return out;
}

/** Bereiche 3/4 (Umsatz-/Gewinnwachstum): CAGR ueber idealerweise idealYears Jahre,
 * degradiert auf bis zu minYears statt komplett auszufallen - reuse der bestehenden
 * cagrOverYearsWithMinimum-Hilfsfunktion (lib/stats.ts). */
function scoreCagrArea(
  key: KpiKey,
  series: YearValue[],
  cfg: { badX: number; goodX: number; idealYears: number; minYears: number },
  metricLabel: string,
): KpiScoreEntry {
  const result = cagrOverYearsWithMinimum(series, cfg.idealYears, cfg.minYears);
  if (!result) {
    return entry(
      key,
      undefined,
      `Für die CAGR von ${metricLabel} liegt keine ausreichende Historie vor (mind. ${cfg.minYears} Jahre nötig).`,
    );
  }
  const { cagr, actualYears } = result;
  const points = gradePoints(cagr, cfg.badX, cfg.goodX);
  return entry(
    key,
    points,
    `${metricLabel} wuchs im Schnitt ${(cagr * 100).toFixed(1)} % p.a. über ${actualYears} verfügbare Jahre - Band von ${(cfg.badX * 100).toFixed(0)} % bis ${(cfg.goodX * 100).toFixed(0)} %.`,
  );
}

/** Bereich 5: Net Debt / EBITDA, aktueller Stand (keine Historie verfuegbar). Negative
 * Nettoverschuldung (Netto-Cash) = volle Punktzahl (die lineare Formel liefert das auch
 * ohne Sonderfall, ein expliziter Zweig liefert aber einen praeziseren Grundtext). EBITDA
 * <= 0 (strukturell z.B. bei reinen Banken/Brokern, die kein EBITDA ausweisen) macht die
 * Kennzahl bedeutungslos - der Bereich faellt dann heraus statt 0 zu geben. Kein
 * Sektor-Adjustment mehr. */
function scoreNetDebtToEbitda(fundamentals: Fundamentals): KpiScoreEntry {
  const cfg = SCORE_CONFIG.thresholds.netDebtToEbitda;
  const netDebt = fundamentals.netDebt;
  const ebitda = fundamentals.ebitda;

  if (netDebt === undefined || ebitda === undefined || ebitda <= 0) {
    return entry("netDebtToEbitda", undefined, "Für Net Debt / EBITDA liegen nicht genügend Daten vor.");
  }
  if (netDebt < 0) {
    return entry("netDebtToEbitda", 10, "Netto-Cash-Position (negative Nettoverschuldung) - volle Punktzahl.");
  }
  const ratio = netDebt / ebitda;
  const points = gradePoints(ratio, cfg.badRatio, cfg.goodRatio);
  return entry(
    "netDebtToEbitda",
    points,
    `Net Debt / EBITDA liegt bei ${ratio.toFixed(2)}x (${cfg.goodRatio.toFixed(1)}x = volle Punktzahl, ${cfg.badRatio.toFixed(1)}x = null Punkte).`,
  );
}

/** Bereich 6: durchschnittliche FCF-Marge (freier Cashflow / Umsatz je Jahr) ueber bis zu
 * 10 Jahre (min. 2). Kein Zusatz-Gate mehr (kumulierter-FCF-Check war Teil des
 * entfallenen Red-Flag-Multiplikators). */
function scoreFcfMargin(historical: HistoricalSeries): KpiScoreEntry {
  const marginSeries = deriveRatioSeries(historical.freeCashFlow, historical.revenue);
  return scoreAverageRatio("fcfMargin", marginSeries, SCORE_CONFIG.thresholds.fcfMargin, "FCF-Marge");
}

/** Bereich 7: Abweichung des aktuellen KGV vom EIGENEN historischen Durchschnitts-KGV
 * (nicht Median, kein Sektor-Vergleich mehr) ueber bis zu 10 Jahre mit positivem KGV
 * (min. 2). Verlustjahre (negatives/undefiniertes KGV) werden sowohl beim aktuellen Wert
 * als auch aus dem historischen Durchschnitt ausgeschlossen - historical.pe kann fuer
 * Verlustjahre negative Werte enthalten (price / negativer EPS). */
function scoreValuation(fundamentals: Fundamentals, historical: HistoricalSeries): KpiScoreEntry {
  const cfg = SCORE_CONFIG.thresholds.valuation;
  const pe = fundamentals.pe;
  if (pe === undefined || pe <= 0) {
    return entry("valuation", undefined, "Für den KGV-Vergleich liegt kein positives aktuelles KGV vor (Verlustjahr).");
  }
  const positiveHistoricalPe = lastNValues(historical.pe, cfg.idealYears).filter((v) => v > 0);
  if (positiveHistoricalPe.length < cfg.minYears) {
    return entry(
      "valuation",
      undefined,
      `Für den eigenen historischen KGV-Durchschnitt liegen keine ${cfg.minYears} Jahre mit positivem KGV vor.`,
    );
  }
  const avgPe = average(positiveHistoricalPe)!;
  const discount = (avgPe - pe) / avgPe;
  const points = gradePoints(discount, cfg.badDiscount, cfg.goodDiscount);
  return entry(
    "valuation",
    points,
    `Aktuelles KGV ${pe.toFixed(1)} liegt ${(Math.abs(discount) * 100).toFixed(0)} % ${discount >= 0 ? "unter" : "über"} dem eigenen Durchschnitts-KGV der letzten ${positiveHistoricalPe.length} verfügbaren Jahre (${avgPe.toFixed(1)}).`,
  );
}

/** Bereich 8 (einziger zweiteiliger Bereich): Durchschnitt aus Rendite-Teilscore
 * (aktuelle TTM-Dividendenrendite, 0 % -> 0 Pkt., >= yieldGoodX -> 10 Pkt.) und
 * Wachstums-Teilscore (Dividenden-CAGR je Aktie ueber bis zu 10, mind. 5 Jahre - laenger
 * als das sonstige Minimum von 2, da eine kuerzere Dividenden-CAGR zu volatil waere).
 * Ohne ausreichende Wachstumshistorie zaehlt nur der Rendite-Teilscore. Zahlt das
 * Unternehmen keine Dividende, ist der GESAMTE Bereich nicht verfuegbar - kein
 * Rueckkauf-/Ersatzwert (Erik-Entscheidung 2026-07-20). */
function scoreCapitalReturn(fundamentals: Fundamentals, historical: HistoricalSeries): KpiScoreEntry {
  const cfg = SCORE_CONFIG.thresholds.capitalReturn;
  const yieldValue = fundamentals.dividendYield;
  if (yieldValue === undefined || yieldValue <= 0) {
    return entry(
      "capitalReturn",
      undefined,
      "Es liegt keine Dividende vor (kein Ersatzwert, z. B. für Aktienrückkäufe).",
    );
  }
  const yieldSub = clamp((10 * yieldValue) / cfg.yieldGoodX, 0, 10);
  const growth = cagrOverYearsWithMinimum(historical.dividendPerShare, cfg.idealYears, cfg.minYears);

  if (!growth) {
    return entry(
      "capitalReturn",
      yieldSub,
      `Dividendenrendite ${(yieldValue * 100).toFixed(1)} % (Ziel ${(cfg.yieldGoodX * 100).toFixed(0)} % für volle Punktzahl) - nur Rendite-Teilscore, da keine ${cfg.minYears} Jahre Dividendenhistorie für einen Wachstums-Teilscore vorliegen.`,
    );
  }

  const growthSub = clamp((10 * growth.cagr) / cfg.dividendCagrGoodX, 0, 10);
  const points = (yieldSub + growthSub) / 2;
  return entry(
    "capitalReturn",
    points,
    `Dividendenrendite ${(yieldValue * 100).toFixed(1)} % (Ziel ${(cfg.yieldGoodX * 100).toFixed(0)} %) und Dividenden-CAGR ${(growth.cagr * 100).toFixed(1)} % p.a. über ${growth.actualYears} verfügbare Jahre (Ziel ${(cfg.dividendCagrGoodX * 100).toFixed(0)} %) - Durchschnitt aus beiden Teilscores.`,
  );
}

/** Bereich 9: Preis-CAGR ueber bis zu 10 Jahre, degradiert auf bis zu 2 Jahre statt
 * komplett auszufallen (vorher: hartes Minimum von 9 Jahren). Auch bei kurzer Historie
 * ist die volle Punktzahl erreichbar - dieselbe Formel gilt unabhaengig von der
 * tatsaechlichen Fensterlaenge. */
function scorePriceCagr(priceHistory: PricePoint[]): KpiScoreEntry {
  const cfg = SCORE_CONFIG.thresholds.priceCagr;
  if (priceHistory.length < 2) {
    return entry("priceCagr", undefined, "Für die Kursperformance liegt keine ausreichende Kurshistorie vor.");
  }
  const sorted = [...priceHistory].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const latestDate = new Date(latest.date);
  const targetDate = new Date(latestDate);
  targetDate.setFullYear(targetDate.getFullYear() - cfg.idealYears);
  const start = sorted.find((p) => new Date(p.date) >= targetDate);
  const spanYears = start
    ? (latestDate.getTime() - new Date(start.date).getTime()) / (365.25 * 24 * 3600 * 1000)
    : 0;

  if (!start || start.close <= 0 || spanYears < cfg.minYears) {
    return entry("priceCagr", undefined, `Für die Kursperformance liegen keine ${cfg.minYears} Jahre Kurshistorie vor.`);
  }
  const cagr = Math.pow(latest.close / start.close, 1 / spanYears) - 1;
  const points = gradePoints(cagr, cfg.badX, cfg.goodX);
  return entry(
    "priceCagr",
    points,
    `Kurs-CAGR über die verfügbaren ${spanYears.toFixed(1)} Jahre (Ziel ${cfg.idealYears}) liegt bei ${(cagr * 100).toFixed(1)} % p.a. - Band von ${(cfg.badX * 100).toFixed(0)} % bis ${(cfg.goodX * 100).toFixed(0)} %.`,
  );
}

/** Bereich 10: Anteil Verlustjahre am betrachteten Fenster (bis zu 10, min. 2 Jahre) -
 * relativ zur betrachteten Jahresanzahl, damit ein kuerzeres Fenster nicht automatisch
 * fairer/unfairer wirkt. "Verlustjahr" = Nettogewinn < 0 (historical.netIncome), als
 * Naeherung fuer EBIT < 0, da im Projekt keine EBIT-Zeitreihe verfuegbar ist (weder
 * Yahoo- noch FMP-/SEC-/Alpha-Vantage-Anbindung liefert eine - Erik-Entscheidung
 * 2026-07-20, siehe docs/features/erikonomie-score.md). */
function scoreLossYears(historical: HistoricalSeries): KpiScoreEntry {
  const cfg = SCORE_CONFIG.thresholds.lossYears;
  const values = lastNValues(historical.netIncome, cfg.idealYears);
  if (values.length < cfg.minYears) {
    return entry("lossYears", undefined, `Für die Bilanzrobustheit liegen keine ${cfg.minYears} Jahre Historie vor.`);
  }
  const lossCount = values.filter((v) => v < 0).length;
  const lossRatio = lossCount / values.length;
  // Progressiv statt linear (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt 13): der
  // quadratische Zusatzterm laesst den Abzug je ZUSAETZLICHEM Verlustjahr steigen statt
  // konstant zu bleiben (Marginalkosten 1 + 2*penaltyAcceleration*lossRatio, linear
  // wachsend) - ein einzelnes Verlustjahr bleibt dabei fast so sanft wie vorher (Anteil
  // 0,1 -> 8,9 statt 9,0 Punkte), mehrere Verlustjahre in Folge kosten spuerbar mehr als
  // vorher (Anteil 0,3 -> 6,1 statt 7,0 Punkte).
  const points = clamp(10 * (1 - lossRatio * (1 + cfg.penaltyAcceleration * lossRatio)), 0, 10);
  return entry(
    "lossYears",
    points,
    `${lossCount} Verlustjahr(e) (Nettogewinn < 0, als Näherung für EBIT < 0) in ${values.length} betrachteten Jahren.`,
  );
}

/** Nicht mehr Teil der Erikonomie-Score-Engine (Score v3, 2026-07-20 - der Red-Flag-
 * Multiplikator wurde ersatzlos aus dem Score entfernt), aber weiterhin exportiert: gilt
 * gleiche Konzept in der Qualitaets-/Moat-Checkliste (lib/quality/checklist.ts) fuer drei
 * ihrer sechs automatischen Kriterien - ein einziger Schwellenwert-Pfad statt eines
 * zweiten, potenziell abweichenden. Nutzt jetzt QUALITY_CHECKLIST_THRESHOLDS statt der
 * (mit v3 aufgeloesten) SCORE_CONFIG.redFlags. */
export function computeRedFlags(
  fundamentals: Fundamentals,
  historical: HistoricalSeries,
  netDebtToEbitdaRatio: number | undefined,
): RedFlags {
  const cfg = QUALITY_CHECKLIST_THRESHOLDS;

  const netDebtToEbitdaTooHigh =
    netDebtToEbitdaRatio !== undefined && netDebtToEbitdaRatio > cfg.netDebtToEbitdaMax;

  const lastFcf = lastNValues(historical.freeCashFlow, cfg.fcfCumulativeLookbackYears);
  const negativeCumulativeFcf =
    lastFcf.length >= 2 && lastFcf.reduce((sum, v) => sum + v, 0) < 0;

  const sharesSorted = [...historical.sharesOutstanding].sort((a, b) => a.year - b.year);
  const latestShares = sharesSorted.at(-1);
  const targetYear = latestShares ? latestShares.year - cfg.shareDilutionLookbackYears : undefined;
  const startShares = targetYear
    ? [...sharesSorted].reverse().find((v) => v.year <= targetYear)
    : undefined;
  const shareDilution =
    !!startShares &&
    !!latestShares &&
    startShares.value > 0 &&
    (latestShares.value - startShares.value) / startShares.value > cfg.shareDilutionMaxIncrease;

  return {
    netDebtToEbitdaTooHigh,
    negativeCumulativeFcf,
    shareDilution,
    anyTriggered: netDebtToEbitdaTooHigh || negativeCumulativeFcf || shareDilution,
  };
}

/**
 * Vermittelt in einem nicht-technischen Satz, WARUM der Score vergeben wurde - bewusst
 * OHNE einzelne Kennzahlen-Namen oder Zahlen (Erik 2026-07-11: "das können die Nutzer
 * selbst darunter bei den Kennzahlen sehen", "nur vermitteln, warum und was das
 * bedeutet"). Nur die Anzahl starker/schwacher Bereiche (nicht ihre Namen) fliesst in die
 * Tonalitaet ein, damit der Text trotzdem pro Aktie unterschiedlich statt austauschbar
 * bleibt. Unveraendert generisch gegenueber der Bereichsanzahl (funktioniert fuer 8 wie
 * fuer 10 Bereiche).
 */
function buildSummary(kpis: KpiScoreEntry[], name?: string): string {
  const available = kpis.filter(
    (k): k is KpiScoreEntry & { basePoints: number } => k.available && k.basePoints !== undefined,
  );
  const strengths = available.filter((k) => k.basePoints >= 7).sort((a, b) => b.basePoints - a.basePoints);
  const weaknesses = available.filter((k) => k.basePoints <= 3).sort((a, b) => a.basePoints - b.basePoints);
  const strengthCount = strengths.length;
  const weaknessCount = weaknesses.length;

  const parts: string[] = [];
  if (available.length === 0) {
    parts.push(
      name
        ? `Zu ${name} liegen aktuell zu wenige verlässliche Kennzahlen für eine fundierte Einordnung vor.`
        : "Für eine fundierte Einordnung liegen aktuell zu wenige verlässliche Kennzahlen vor.",
    );
  } else if (weaknessCount === 0 && strengthCount === available.length) {
    parts.push(
      name
        ? `${name} überzeugt in jedem untersuchten Bereich.`
        : "Ein Unternehmen, das in jedem untersuchten Bereich überzeugt.",
    );
  } else if (weaknessCount === 0 && strengthCount > 0) {
    parts.push(
      name
        ? `${name} überzeugt in den untersuchten Bereichen größtenteils, ohne eine echte Schwachstelle.`
        : "Ein Unternehmen, das in den untersuchten Bereichen größtenteils überzeugt, ohne eine echte Schwachstelle.",
    );
  } else if (strengthCount === 0 && weaknessCount === available.length) {
    parts.push(
      name
        ? `Bei ${name} liegt die fundamentale Verfassung in jedem untersuchten Bereich unter dem, was für finanziell robuste Unternehmen typisch ist.`
        : "Ein Unternehmen, dessen fundamentale Verfassung in jedem untersuchten Bereich unter dem liegt, was für finanziell robuste Unternehmen typisch ist.",
    );
  } else if (weaknessCount > 0 && strengthCount === 0) {
    parts.push(
      name
        ? `Bei ${name} liegt die fundamentale Verfassung in mehreren untersuchten Bereichen unter dem, was für finanziell robuste Unternehmen typisch ist.`
        : "Ein Unternehmen, dessen fundamentale Verfassung in mehreren untersuchten Bereichen unter dem liegt, was für finanziell robuste Unternehmen typisch ist.",
    );
  } else if (strengthCount > 0 && weaknessCount > 0) {
    if (strengthCount > weaknessCount * 2) {
      parts.push(
        name
          ? `${name} ist insgesamt überdurchschnittlich stark aufgestellt, mit nur vereinzelten Schwachstellen.`
          : "Ein insgesamt überdurchschnittlich stark aufgestelltes Unternehmen mit nur vereinzelten Schwachstellen.",
      );
    } else if (weaknessCount > strengthCount * 2) {
      parts.push(
        name
          ? `${name} zeigt überwiegende Schwächen, wenngleich auch einzelne Stärken nicht fehlen.`
          : "Ein Unternehmen mit überwiegenden Schwächen, dem aber auch einzelne Stärken nicht fehlen.",
      );
    } else {
      parts.push(
        name
          ? `${name} zeigt ein gemischtes Bild aus überzeugenden und schwächeren Bereichen, ohne klare Gesamttendenz.`
          : "Ein gemischtes Bild aus überzeugenden und schwächeren Bereichen, ohne klare Gesamttendenz.",
      );
    }
  } else {
    parts.push(
      name
        ? `Bei ${name} liegen die untersuchten Bereiche überwiegend im mittleren, unauffälligen Bereich.`
        : "Die untersuchten Bereiche liegen überwiegend im mittleren, unauffälligen Bereich.",
    );
  }

  const strengthSentence = describeThemes(strengths.slice(0, 2), "Besonders stark");
  if (strengthSentence) parts.push(strengthSentence);
  const weaknessSentence = describeThemes(weaknesses.slice(0, 2), "Schwächer");
  if (weaknessSentence) parts.push(weaknessSentence);

  return parts.join(" ");
}

/**
 * Erikonomie-Score v3 (2026-07-20): 10 gleich gewichtete Bereiche, je 0-10 Punkte, aus
 * ausschliesslich unternehmenseigenen Kennzahlen (kein Sektor-Median, kein separater
 * Screener-Call). Gesamtscore = Durchschnitt der Punkte ueber die VERFUEGBAREN Bereiche,
 * hochskaliert auf 0-100 - fehlende Bereiche fallen aus Summe UND Teiler heraus statt mit
 * 0 gewertet zu werden (siehe docs/features/erikonomie-score.md fuer die vollstaendige
 * Herleitung jedes Bereichs). Kein nachtraeglicher Red-Flag-Multiplikator mehr.
 */
export function computeScore(input: ScoringInput): ScoreResult {
  const { fundamentals, historical, priceHistory, name } = input;
  const cfg = SCORE_CONFIG.thresholds;

  const kpis: KpiScoreEntry[] = [
    scoreAverageRatio("netMargin", historical.netMargin, cfg.netMargin, "Nettomarge"),
    scoreAverageRatio("roic", historical.roic, cfg.roic, "ROIC"),
    scoreCagrArea("revenueCagr", historical.revenue, cfg.revenueCagr, "Umsatz"),
    scoreCagrArea("epsCagr", historical.eps, cfg.epsCagr, "Gewinn je Aktie"),
    scoreNetDebtToEbitda(fundamentals),
    scoreFcfMargin(historical),
    scoreValuation(fundamentals, historical),
    scoreCapitalReturn(fundamentals, historical),
    scorePriceCagr(priceHistory),
    scoreLossYears(historical),
  ];

  const available = kpis.filter(
    (k): k is KpiScoreEntry & { basePoints: number } => k.available && k.basePoints !== undefined,
  );
  const total =
    available.length > 0
      ? clamp(Math.round((available.reduce((sum, k) => sum + k.basePoints, 0) / available.length) * 10), 0, 100)
      : 0;

  return {
    total,
    kpis,
    dataQuality: available.length,
    summary: buildSummary(kpis, name),
  };
}
